import { useCallback, useMemo, useRef, useState } from 'react';

import { ChevronLeft, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { DataTableEndlessPaginated } from '@/components/data-table/data-table-endless-paginated';
import { DataTableFilters } from '@/components/data-table/filters-v1/data-table-filters';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { displayToastSuccess } from '@/helpers';
import { useDataTable } from '@/hooks/use-data-table';
import { useFilterValues } from '@/hooks/use-filter-values';
import { TableIds } from '@/lib/constants/table-ids';
import { usePermissions } from '@/modules/common';
import { ConfirmationModal } from '@/modules/common/components/modals/confirmation-modal';
import { ErrorState } from '@/modules/common/components/organisms/error-state';
import { FeatureMode } from '@/modules/common/components/templates';
import { useApiError } from '@/modules/common/hooks/use-api-error';
import { ExtendDirection } from '@/modules/common/types/extend-direction';
import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';
import { getProjectsChangesTabFiltersConfig } from '@/modules/projects';
import { DeleteChangeModal } from '@/modules/projects/components/modals/resolve-conflicts-modal/delete-change-modal';
import { getProjectChangesColumns } from '@/modules/projects/constants/project-changes-table-columns';
import { useDeleteProjectChange } from '@/modules/projects/hooks/use-delete-project-change';
import { useProjectChanges } from '@/modules/projects/hooks/use-project-changes';
import { ProjectTab } from '@/modules/projects/types';
import { ChangeDeletionConflictData } from '@/modules/projects/types/project-change-deletion';
import { ProjectChangeLogEntry, ProjectChangeType } from '@/modules/projects/types/project-changes';
import {
  analyzeChangeDeletion,
  buildExtensionOptions,
  findNextChange,
  findPreviousChange,
} from '@/modules/projects/utils/project-changes/change-deletion.utils';
import { AuthenticatedRoutes } from '@/router/routes';
import { logger } from '@/utils/logger/logger';

import { Project } from '../../../types/project';

const TAG = 'ProjectChangesTab:';

interface ProjectChangesTabProps {
  projectData?: Project;
  mode?: FeatureMode;
}

export const ProjectChangesTab = ({ projectData, mode }: ProjectChangesTabProps) => {
  const { t } = useTranslation();
  const { getErrorMessage, displayToastErrorFromApiWithFallback } = useApiError();
  const navigate = useNavigate();
  const filterConfig = getProjectsChangesTabFiltersConfig();
  const { values: appliedFilters } = useFilterValues(filterConfig);

  const [deleteConflictData, setDeleteConflictData] = useState<ChangeDeletionConflictData | null>(
    null
  );
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSimpleDeleteConfirm, setShowSimpleDeleteConfirm] = useState(false);
  const [changeToDelete, setChangeToDelete] = useState<{
    change: ProjectChangeLogEntry;
    extendDirection: ExtendDirection;
  } | null>(null);
  const deleteChangeMutation = useDeleteProjectChange();

  const {
    data: changesData,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useProjectChanges({
    projectId: projectData?.id || 0,
    filters: appliedFilters,
    enabled: !!projectData?.id,
  });

  // Keep a stable ref so handleDeleteClick doesn't change when items refresh,
  // which would recreate columns and cause avatar blink on every re-render.
  const changesItemsRef = useRef(changesData?.items);
  changesItemsRef.current = changesData?.items;

  const handleEditClick = useCallback(
    (row: ProjectChangeLogEntry) => {
      logger.debug(TAG + 'handleEditClick', row);
      const tab = row.type === ProjectChangeType.Info ? ProjectTab.Info : ProjectTab.Billing;
      // changeId is only unique within a section (separate id sequence per section on the
      // backend), so the target section must travel with the link to disambiguate collisions —
      // e.g. the initial project-creation change writes an Info row and a Billing row that can
      // end up sharing the same id.
      navigate(`/projects/${projectData?.id}/changes/${row.id}?tab=${tab}&changeType=${row.type}`);
    },
    [navigate, projectData?.id]
  );

  const handleDeleteClick = useCallback(
    async (row: ProjectChangeLogEntry) => {
      logger.debug(TAG + 'handleDeleteClick', row);

      const items = changesItemsRef.current;
      if (!items || !projectData?.id) return;

      const conflictAnalysis = analyzeChangeDeletion(items, row, t);

      if (!conflictAnalysis) {
        // No conflict — either 0 or 1 extension option exists.
        // Use that option's direction, defaulting to Previous (extends the earlier change).
        const previousChange = findPreviousChange(items, row);
        const nextChange = findNextChange(items, row);
        const options = buildExtensionOptions(t, previousChange, nextChange, row);
        const extendDirection =
          options.length === 1 ? options[0].direction : ExtendDirection.Previous;

        setChangeToDelete({ change: row, extendDirection });
        setShowSimpleDeleteConfirm(true);
      } else {
        // Show conflict resolution modal for user to choose extend direction
        setDeleteConflictData(conflictAnalysis);
        setShowDeleteModal(true);
      }
    },
    [projectData?.id, t]
  );

  const handleDeleteSuccess = useCallback(() => {
    setShowDeleteModal(false);
    setDeleteConflictData(null);
    toast.success(t('authenticated.projects:details.tabs.changes.deleteModal.deleteSuccess'));
  }, [t]);

  const { hasPermission } = usePermissions();
  const isEditProjectEnabled = hasPermission(OpsiumPolicy.ProjectsEdit);
  const isEditProjectBillingEnabled = hasPermission(OpsiumPolicy.ProjectsBillingManage);
  const handleSimpleDeleteConfirm = useCallback(
    async (closeModal: () => void) => {
      if (!changeToDelete || !projectData?.id) return;

      try {
        await deleteChangeMutation.mutateAsync({
          projectId: projectData.id,
          section: changeToDelete.change.type,
          effectiveStartDate: changeToDelete.change.startDate,
          extendDirection: changeToDelete.extendDirection,
        });
        displayToastSuccess(
          t('authenticated.projects:details.tabs.changes.deleteModal.deleteSuccess')
        );
        closeModal();
        setChangeToDelete(null);
      } catch (error) {
        displayToastErrorFromApiWithFallback(
          error,
          'authenticated.projects:details.tabs.changes.deleteModal.deleteError'
        );
      }
    },
    [changeToDelete, projectData?.id, deleteChangeMutation, t, displayToastErrorFromApiWithFallback]
  );

  const columns = useMemo(
    () =>
      getProjectChangesColumns({
        handleEditClick,
        handleDeleteClick,
        isEditProjectEnabled,
        isEditProjectBillingEnabled,
        mode,
      }),
    [handleEditClick, handleDeleteClick, mode, isEditProjectEnabled, isEditProjectBillingEnabled]
  );

  const { table, saveColumnSizing } = useDataTable<ProjectChangeLogEntry>({
    data: changesData?.items || [],
    columns,
    tableId: TableIds.PROJECT_CHANGES,
    getRowId: row => `${row.id}`,
  });

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const getRowClassName = useCallback((row: ProjectChangeLogEntry) => {
    return row.isActive ? '!bg-success/5 hover:!bg-success/5' : '';
  }, []);

  if (error) {
    return (
      <ErrorState
        error={getErrorMessage(error)}
        className="min-h-[400px]"
        actions={[
          {
            label: t('common:retry'),
            onClick: () => refetch(),
            variant: 'default',
            icon: <RefreshCw className="h-4 w-4" />,
          },
          {
            label: t('common:back'),
            onClick: () => navigate(AuthenticatedRoutes.Projects),
            variant: 'outline',
            icon: <ChevronLeft className="h-4 w-4" />,
          },
        ]}
      />
    );
  }

  return (
    <Card className="flex max-h-[calc(100vh-100px)] flex-col">
      <CardHeader className="shrink-0">
        <CardTitle>{t('authenticated.projects:details.tabs.changes.changeHistory')}</CardTitle>
      </CardHeader>
      <CardContent className="flex min-h-0 flex-1 flex-col gap-4">
        {/* Filters section */}
        <div className="shrink-0">
          <DataTableFilters filtersConfig={filterConfig} className="flex flex-wrap gap-2" />
        </div>

        {/* Total count */}
        <p className="shrink-0 text-xs text-muted-foreground">
          {changesData?.pagination?.total ?? 0}{' '}
          {t('authenticated.projects:details.tabs.changes.changes').toLowerCase()}
        </p>

        {/* Scrollable table - must have min-h-0 and flex-1 for stickyHeader to work */}
        <DataTableEndlessPaginated
          table={table}
          isLoading={isLoading || isFetchingNextPage}
          hasMore={hasNextPage || false}
          onLoadMore={handleLoadMore}
          getRowClassName={getRowClassName}
          stickyHeader
          tableId={TableIds.PROJECT_CHANGES}
          onResizeEnd={saveColumnSizing}
        />
      </CardContent>

      {/* Simple delete confirmation modal */}
      <ConfirmationModal
        title={t('authenticated.projects:details.tabs.changes.deleteModal.confirmTitle')}
        message={t('authenticated.projects:details.tabs.changes.deleteModal.confirmMessage')}
        confirmText={t('authenticated.projects:details.tabs.changes.deleteModal.delete')}
        confirmVariant="destructive"
        open={showSimpleDeleteConfirm}
        onOpen={() => setShowSimpleDeleteConfirm(true)}
        onClose={() => {
          setShowSimpleDeleteConfirm(false);
          setChangeToDelete(null);
        }}
        onConfirm={handleSimpleDeleteConfirm}
        isLoading={deleteChangeMutation.isPending}
      />

      {/* Conflict resolution modal (when multiple extend options) */}
      {deleteConflictData && (
        <DeleteChangeModal
          conflictData={deleteConflictData}
          projectId={projectData?.id || 0}
          onSuccess={handleDeleteSuccess}
          open={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setDeleteConflictData(null);
          }}
        />
      )}
    </Card>
  );
};
